import React, { useEffect } from 'react';
import { useRouter } from 'next/router';
import dynamic from 'next/dynamic';
import { NextSeo } from 'next-seo';
import { v4 as uuidv4 } from 'uuid';
import { GetServerSidePropsContext } from 'next';
import { useTranslation } from 'next-i18next';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import Cookies from 'js-cookie';

import { ALGOLIA_CHECKOUT_QUERY_ID_NAME } from '@adapters/bigcommerce/constants';
import getLoggedInCustomer from '@adapters/bigcommerce/operations/customer/getLoggedInCustomer';
import getAllPromoTickerData from '@adapters/prismic/fetching/promos/getAllPromoTickerData';
import getBannerStripGlobalData from '@adapters/prismic/fetching/banner-strip-global/getBannerStripGlobalData';
import { getAllStoresDataForSetMyStore } from '@adapters/prismic/fetching/stores';

import { LocaleOverrideContext } from '@hooks/useLocale';
import { SsrPriceGroupContext } from '@hooks/usePriceGroupKey';
import {
  getGroupKeyByCustomerGroupId,
  PriceGroupKey,
} from '@utils/selectPriceByGroup';
import { altLocale } from '@utils/locale';
import { localeAltPathAbs, localePathAbs } from '@utils/url';
import { getIndex } from '@utils/algolia/analytics';
import { generateAlgoliaSearchKey } from '@utils/algolia/securedKey';

import Layout from '@components/global/Layout';
import Container from '@components/global/Container';
import Breadcrumbs from '@components/global/Breadcrumbs';
import BreadcrumbsV2, { BreadcrumbItem } from '_components/Breadcrumbs';
import BreadcrumbsJsonLd from '_components/Breadcrumbs/BreadcrumbsJsonLd';
import breadcrumbOverrides from '_components/Breadcrumbs/Breadcrumbs.pageOverrides.module.scss';
import { SearchResults } from '@components/algolia';
const CompareBar = dynamic(() => import('_components/CompareBar'), {
  loading: () => null,
});

import { renderToString } from 'react-dom/server';
import {
  getServerState,
  InstantSearchServerState,
  InstantSearchSSRProvider,
} from 'react-instantsearch';
import { getSelectorsByUserAgent } from 'react-device-detect';
import getProductTagInfoData from '@adapters/prismic/fetching/productTagInfo/getProductTagInfo';
import transformLocale from '@utils/transform-locale';
import { getPreviewRef } from '@adapters/prismic/utils/getPreviewRef';
import getPromoBarData from '@adapters/prismic/fetching/promos/getPromoBarData';
import {
  FEATURE_FLAG_BREADCRUMBS_V2,
  FEATURE_FLAG_PLP_FULL_WIDTH,
  FEATURE_FLAG_PROMO_BAR_SERVER_SIDE,
} from '@config/feature-flag';
import { withShopperContextSSP } from '@adapters/bigcommerce/shopperContext';

const RecommendationsLoader = dynamic(
  () => import('@components/global/Recommendations/Loader'),
  {
    loading: () => null,
    ssr: false,
  }
);

type SearchPageProps = {
  serverUrl: string;
  serverState?: InstantSearchServerState;
  canonicalUrl: string;
  langAltUrl: string;
  xDefault: string;
  query: string;
  isMobile: boolean;
  algoliaQueryID: string | null;
  priceGroupKey: PriceGroupKey;
};

async function getPageProps({
  req,
  res,
  params,
  query,
  previewData,
  preview = false,
}: GetServerSidePropsContext) {
  const locale = params?.locale === 'fr' ? 'fr' : 'en';
  if (!locale) {
    throw new Error('No locale specified for page');
  }
  const notDefaultLocale = transformLocale(locale);

  const { q } = query as { q?: string };
  const titleFallback =
    notDefaultLocale === 'en' ? 'All products' : 'Tous les produits';
  const searchTitle = q ? q : titleFallback;

  const previewRef = getPreviewRef(preview, previewData);

  const [
    stores,
    promoMessages,
    bannerStripData,
    serverSideTranslation,
    productTagInfoData,
    promoBarData,
  ] = await Promise.allSettled([
    getAllStoresDataForSetMyStore(notDefaultLocale),
    !FEATURE_FLAG_PROMO_BAR_SERVER_SIDE &&
      notDefaultLocale &&
      getAllPromoTickerData(notDefaultLocale),
    getBannerStripGlobalData(
      notDefaultLocale,
      preview ? previewRef : undefined
    ),
    serverSideTranslations(notDefaultLocale, [
      'common',
      'searchResults',
      'promotions',
      'comparison',
      'plp',
      'quickAdd',
    ]),
    getProductTagInfoData(notDefaultLocale),
    FEATURE_FLAG_PROMO_BAR_SERVER_SIDE &&
      notDefaultLocale &&
      getPromoBarData(notDefaultLocale),
  ]).then((results) =>
    results.map((promise) => {
      if (
        promise.status === 'rejected' ||
        typeof promise.value === 'undefined'
      ) {
        console.error('promise rejected or returning undefined', promise);
        return null;
      }
      return promise.value;
    })
  );

  const host = req?.headers?.host || '';
  const protocol = host.includes('localhost') ? 'http' : 'https';

  const path = req?.url || '';
  const requestUrl = host.includes('localhost')
    ? `${notDefaultLocale}${path}`
    : path;

  const fullUrl = new URL(`${protocol}://${host}/${requestUrl}`);
  fullUrl.searchParams.delete('query');

  const baseUrl = fullUrl.origin ?? 'https://www.mec.ca';
  const trimmedPathname = fullUrl.pathname.replace(/\/$/, '');
  const serverUrl = `${baseUrl}${trimmedPathname}${fullUrl.search}`;

  const userAgent = req.headers['user-agent'] || '';
  const { isMobile } = getSelectorsByUserAgent(userAgent);

  const langAltUrl = localeAltPathAbs(notDefaultLocale, `/search`) ?? '';
  // Canonical/hreflang must point to the clean, production base URL
  // (BASE_URL → https://www.mec.ca) — never req.headers.host (which is
  // bc-prod.mec.ca on Vercel) and never with filter/query params. All search
  // URLs consolidate to /{locale}/search. serverUrl keeps the real request
  // URL (with params) for InstantSearch SSR routing/hydration only.
  const canonicalUrl = localePathAbs(notDefaultLocale, `/search`);
  const xDefault = notDefaultLocale === 'en' ? canonicalUrl : langAltUrl;
  const existingUserToken = req.cookies._ALGOLIA;
  const userToken = existingUserToken || `anonymous-${uuidv4()}`;

  if (!existingUserToken) {
    const existingSetCookie = res.getHeader('Set-Cookie');
    const setCookieHeader = Array.isArray(existingSetCookie)
      ? existingSetCookie
      : existingSetCookie
      ? [String(existingSetCookie)]
      : [];

    setCookieHeader.push(
      `_ALGOLIA=${encodeURIComponent(
        userToken
      )}; Path=/; HttpOnly; SameSite=Lax; Secure`
    );

    res.setHeader('Set-Cookie', setCookieHeader);
  }

  const { searchKey: algoliaSearchKey, expiresAt: algoliaSearchKeyExpiresAt } =
    generateAlgoliaSearchKey(userToken);

  // Resolve the customer's pricing context server-side so price sort/filter
  // attributes are correct in the SSR markup and on the first client render
  // (avoids the guest→tier flip + extra Algolia query on hard reload). Guests
  // have no customer token, so this returns null without a network call.
  let priceGroupKey: PriceGroupKey = 'guest';
  try {
    const loggedInCustomer = await getLoggedInCustomer(req);
    if (loggedInCustomer?.customerGroupId) {
      priceGroupKey = getGroupKeyByCustomerGroupId(
        loggedInCustomer.customerGroupId
      );
    }
  } catch (error) {
    console.error('Failed to resolve pricing group for SSR', error);
  }

  const serverState = await getServerState(
    <LocaleOverrideContext.Provider value={notDefaultLocale}>
      <SsrPriceGroupContext.Provider value={priceGroupKey}>
        <SearchResults
          serverUrl={serverUrl}
          locale={notDefaultLocale}
          isMobile={isMobile}
          title={searchTitle}
          pttTiles={[]}
          productLineCarousel={{ items: [] }}
          userToken={userToken}
          algoliaSearchKey={algoliaSearchKey}
        />
      </SsrPriceGroupContext.Provider>
    </LocaleOverrideContext.Provider>,
    { renderToString }
  );

  const indexName = getIndex(notDefaultLocale);
  const algoliaQueryID =
    serverState?.initialResults[indexName]?.results?.[0]?.queryID;

  return {
    props: {
      stores,
      canonicalUrl,
      langAltUrl,
      xDefault,
      ...serverSideTranslation,
      promoMessages,
      bannerStripData,
      serverState,
      serverUrl,
      query: searchTitle,
      isMobile,
      productTagInfoData,
      algoliaQueryID,
      promoBarData,
      priceGroupKey,
      algoliaSearchKey: {
        appId: process.env.NEXT_PUBLIC_ALGOLIA_APP_ID || '',
        searchKey: algoliaSearchKey,
        userToken,
        expiresAt: algoliaSearchKeyExpiresAt,
      },
    },
  };
}

export const getServerSideProps = withShopperContextSSP(getPageProps);

export default function SearchPage({
  serverUrl,
  serverState,
  canonicalUrl,
  langAltUrl,
  xDefault,
  query,
  isMobile,
  algoliaQueryID,
  priceGroupKey,
}: SearchPageProps) {
  const { t } = useTranslation(['searchResults']);
  const { t: tCommon } = useTranslation('common');
  const { query: routerQuery } = useRouter();
  const locale = (routerQuery.locale as string) || 'en';

  const metaTitle = t('collection.title', { name: query });
  const metaDescription = t('collection.description', { name: query });

  // `href: null` marks this as the current page, so it renders as a
  // non-interactive `aria-current="page"` crumb.
  const breadcrumbItems: BreadcrumbItem[] = [
    {
      id: 'breadcrumb-search',
      label: tCommon('breadcrumb.search'),
      href: null,
    },
  ];

  useEffect(() => {
    if (algoliaQueryID) {
      Cookies.set(ALGOLIA_CHECKOUT_QUERY_ID_NAME, algoliaQueryID);
    }
  }, [algoliaQueryID]);

  return (
    <>
      <RecommendationsLoader
        pageType="search"
        data={{
          searchTerm: query,
        }}
        trigger={query !== null}
      />
      {/*
        Internal search-results pages are noindex,follow: Google discourages
        indexing site search, and a blanket noindex keeps all signals consistent
        with the canonical (which consolidates every search URL to
        /{locale}/search). follow stays on so link equity flows to products.
      */}
      <NextSeo
        title={metaTitle}
        description={metaDescription}
        canonical={canonicalUrl}
        languageAlternates={[
          { hrefLang: altLocale(locale), href: langAltUrl },
          { hrefLang: String(locale), href: canonicalUrl },
          { hrefLang: 'x-default', href: xDefault },
        ]}
        noindex
        nofollow={false}
        openGraph={{ title: metaTitle, description: metaDescription, locale }}
      />
      <Container fullWidth={FEATURE_FLAG_PLP_FULL_WIDTH}>
        {FEATURE_FLAG_BREADCRUMBS_V2 ? (
          <>
            <BreadcrumbsJsonLd items={breadcrumbItems} />
            <BreadcrumbsV2
              items={breadcrumbItems}
              className={breadcrumbOverrides.inContainer}
            />
          </>
        ) : (
          <Breadcrumbs name="Search" />
        )}
        <InstantSearchSSRProvider {...serverState}>
          <SsrPriceGroupContext.Provider value={priceGroupKey}>
            <SearchResults
              serverUrl={serverUrl}
              locale={locale}
              title={query}
              isMobile={isMobile}
              pttTiles={[]}
              productLineCarousel={null}
            />
          </SsrPriceGroupContext.Provider>
        </InstantSearchSSRProvider>
        <CompareBar />
      </Container>
    </>
  );
}

SearchPage.Layout = Layout;
